import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Ban,
  Clock,
  Copy,
  ExternalLink,
  Eye,
  Navigation,
  PackageCheck,
  RefreshCw,
  RotateCcw,
  Truck,
  UserMinus,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { VENUE_EVENTS } from '@/lib/shared-types';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { useBranches, useBranchRooms, useQueryParams, formatDateTime } from '@/Pages/Operations/shared';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Field, Select } from '@/components/ui/field';
import { Dialog } from '@/components/ui/dialog';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  ACTIVE_DELIVERY_STATUSES,
  DELIVERY_PERMISSIONS,
  DELIVERY_ROUTES,
  DELIVERY_STATUSES,
  TERMINAL_DELIVERY_STATUSES,
  apiMessage,
  trackingLinkFor,
  type Delivery,
  type Driver,
} from '../deliveryApi';
import Money from './Money';
import PanelResult from './PanelResult';
import DeliverDialog from './DeliverDialog';
import ResolveReturnDialog from './ResolveReturnDialog';

/**
 * How often the board re-reads itself while somebody is looking at it, and
 * only while at least one delivery on screen is road-active.
 *
 * `delivery.status_changed` IS forwarded to a browser (`RealtimeService`
 * carries a real `@OnEvent` for it — checked in the backend source, not
 * assumed), so every status edge below is realtime, not polled. A driver's
 * *position* is not: `delivery.location_updated` is reserved in the shared
 * `VENUE_EVENTS` table but nothing on the backend ever emits it today, and
 * `RealtimeService` has no handler for it either — subscribing here would
 * join a room and wait forever. So position freshness is the one thing this
 * board polls for, on a slow interval, and only while it matters (an active
 * delivery is actually on screen) — it stops the moment nothing is moving.
 */
const POSITION_POLL_MS = 20_000;

const STATUS_TONE: Record<string, string> = {
  PENDING: 'bg-line-strong/40 text-ink',
  ASSIGNED: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300',
  PICKED_UP: 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-300',
  EN_ROUTE: 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300',
  DELIVERED: 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300',
  CANCELLED: 'bg-line-strong/30 text-ink-muted',
};

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex rounded px-2 py-0.5 text-xs font-medium', STATUS_TONE[status])}>
      {t(`delivery.status.${status}`)}
    </span>
  );
}

function minutesAgo(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now - then) / 60_000));
}

/**
 * ## The dispatch board
 *
 * The screen a dispatcher opens at the start of a shift and leaves running —
 * every currently-relevant delivery, who it is assigned to, and the one
 * action that moves it forward. This is deliberately NOT the same screen as
 * driver cash reconciliation (`DriverShiftsPanel`, a separate tab): a
 * dispatcher watching this board and a manager closing a driver's shift at
 * handover are different people making a decision at a different moment, and
 * this component never reads or writes a `DriverShift`.
 *
 * **Manual status progression exists here on purpose.** ADR-0029 explicitly
 * builds no driver client (§3), and the product blueprint's own surface
 * matrix names "Driver app" as a surface nobody has built. Until one exists,
 * `deliveries.update_status` — pick up, en route, delivered, failed — has no
 * caller anywhere except a dispatcher acting on a driver's phoned-in report,
 * so those actions live here rather than being coverage this Dashboard
 * quietly skips.
 */
export default function DispatchBoardPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm();
  const branches = useBranches();

  const canDispatch = can(DELIVERY_PERMISSIONS.deliveriesDispatch);
  const canUpdateStatus = can(DELIVERY_PERMISSIONS.deliveriesUpdateStatus);
  const canManageReturn = can(DELIVERY_PERMISSIONS.deliveriesManageReturn);
  const canSeeDrivers = can(DELIVERY_PERMISSIONS.driversView);

  const [branchId, setBranchId] = useState('');
  const [status, setStatus] = useState('');
  const [driverId, setDriverId] = useState('');
  const [live, setLive] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [assignTarget, setAssignTarget] = useState<Delivery | null>(null);
  const [deliverTarget, setDeliverTarget] = useState<Delivery | null>(null);
  const [returnTarget, setReturnTarget] = useState<Delivery | null>(null);
  const [detailTarget, setDetailTarget] = useState<Delivery | null>(null);

  const filtered = Boolean(branchId || status || driverId);
  const clearFilters = () => {
    setBranchId('');
    setStatus('');
    setDriverId('');
  };

  const params = useQueryParams({ branchId, status, driverId, limit: 100 });
  const deliveriesQuery = useApi<unknown>(DELIVERY_ROUTES.deliveries, params);
  const driversQuery = useApi<unknown>(
    canSeeDrivers ? DELIVERY_ROUTES.drivers : null,
    useQueryParams({ status: 'ACTIVE', limit: 200 }),
  );

  const deliveries = rowsOf<Delivery>(deliveriesQuery.data).rows;
  const drivers = rowsOf<Driver>(driversQuery.data).rows;

  const branchName = useMemo(() => {
    const map = new Map(branches.map((b) => [b.id, b.name]));
    return (id: string) => map.get(id) ?? id;
  }, [branches]);
  const driverName = useMemo(() => {
    const map = new Map(
      drivers.map((d) => [d.id, d.user ? `${d.user.firstName} ${d.user.lastName}`.trim() : d.id]),
    );
    return (id: string | null) => (id ? (map.get(id) ?? t('delivery.board.unknownDriver')) : null);
  }, [drivers, t]);

  const reload = useCallback(() => {
    deliveriesQuery.reload();
    if (canSeeDrivers) driversQuery.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveriesQuery.reload, driversQuery.reload, canSeeDrivers]);

  // Every branch the actor can see, plus the selected filter, plus whatever
  // branches are already on screen — an empty board still has to hear the
  // FIRST delivery of the day, which is the reason this is not derived from
  // `deliveries` alone. See `useBranchRooms`'s own doc comment.
  const rooms = useBranchRooms(branchId || null, deliveries);
  useRealtime(VENUE_EVENTS.DELIVERY_STATUS_CHANGED, reload, rooms);
  useRealtimeResync(reload);

  const hasActive = deliveries.some((d) => ACTIVE_DELIVERY_STATUSES.includes(d.status));
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    if (!live || !hasActive) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      reloadRef.current();
    }, POSITION_POLL_MS);
    return () => window.clearInterval(id);
  }, [live, hasActive]);

  const now = Date.now();

  const act = async (id: string, run: () => Promise<unknown>, success: string) => {
    setBusyId(id);
    try {
      await run();
      toast.success(success);
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.board.actionFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const unassign = (d: Delivery) =>
    act(d.id, () => api.post(DELIVERY_ROUTES.unassign(d.id)), t('delivery.board.unassigned'));

  const pickUp = (d: Delivery) =>
    act(d.id, () => api.post(DELIVERY_ROUTES.pickUp(d.id)), t('delivery.board.pickedUp'));

  const enRoute = (d: Delivery) =>
    act(d.id, () => api.post(DELIVERY_ROUTES.enRoute(d.id)), t('delivery.board.enRoute'));

  const cancel = async (d: Delivery) => {
    const ok = await confirm.confirm({
      title: t('delivery.board.cancelTitle'),
      body: t('delivery.board.cancelConfirm'),
      confirmLabel: t('delivery.board.cancel'),
      tone: 'danger',
    });
    if (!ok) return;
    await act(d.id, () => api.post(DELIVERY_ROUTES.cancel(d.id)), t('delivery.board.cancelled'));
  };

  const fail = async (d: Delivery) => {
    const reason = await confirm.prompt({
      title: t('delivery.board.failTitle'),
      body: t('delivery.board.failConfirm'),
      label: t('delivery.board.failReason'),
      placeholder: t('delivery.board.failReasonPlaceholder'),
      confirmLabel: t('delivery.board.failConfirmLabel'),
      tone: 'danger',
      validate: (value) => (value.trim() ? null : t('delivery.board.failReasonRequired')),
    });
    if (reason === null) return;
    await act(
      d.id,
      () => api.post(DELIVERY_ROUTES.fail(d.id), { failureReason: reason.trim() }),
      t('delivery.board.failed'),
    );
  };

  const copyTrackingLink = async (d: Delivery) => {
    const link = trackingLinkFor(d.trackingToken);
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('delivery.board.trackingLinkCopied'));
    } catch {
      toast.error(t('delivery.board.trackingLinkCopyFailed'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {branches.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label={t('delivery.board.branch')}
          >
            <option value="">{t('delivery.board.allBranches')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        )}
        <Select
          className="w-auto min-w-[10rem]"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t('delivery.board.status')}
        >
          <option value="">{t('delivery.board.allStatuses')}</option>
          {DELIVERY_STATUSES.map((option) => (
            <option key={option} value={option}>
              {t(`delivery.status.${option}`)}
            </option>
          ))}
        </Select>
        {canSeeDrivers && drivers.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
            aria-label={t('delivery.board.driver')}
          >
            <option value="">{t('delivery.board.allDrivers')}</option>
            {drivers.map((driver) => (
              <option key={driver.id} value={driver.id}>
                {driverName(driver.id)}
              </option>
            ))}
          </Select>
        )}

        <div className="ms-auto flex items-center gap-2">
          <span className="text-xs text-ink-muted" aria-live="polite">
            {deliveriesQuery.loading
              ? t('delivery.board.refreshing')
              : live
                ? t('delivery.board.liveStatus')
                : t('delivery.board.paused')}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setLive((v) => !v)}
            aria-pressed={live}
            title={live ? t('delivery.board.pause') : t('delivery.board.resume')}
          >
            <Icon as={live ? Clock : RotateCcw} />
          </Button>
          <Button size="sm" variant="outline" onClick={reload} disabled={deliveriesQuery.loading}>
            <Icon as={RefreshCw} />
            {t('delivery.board.refresh')}
          </Button>
        </div>
      </div>

      <PanelResult
        loading={deliveriesQuery.loading && !deliveriesQuery.data}
        error={deliveriesQuery.error}
        count={deliveries.length}
        onRetry={reload}
        subject={t('delivery.board.subject')}
        filtered={filtered}
        onClearFilters={clearFilters}
        emptyTitle={t('delivery.board.emptyTitle')}
        emptyHint={t('delivery.board.emptyHint')}
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('delivery.board.status')}</TH>
                <TH>{t('delivery.board.order')}</TH>
                <TH>{t('delivery.board.branch')}</TH>
                <TH>{t('delivery.board.driver')}</TH>
                <TH>{t('delivery.board.fee')}</TH>
                <TH>{t('delivery.board.position')}</TH>
                <TH>{t('delivery.board.updated')}</TH>
                <TH className="text-end">{t('delivery.board.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {deliveries.map((d) => {
                const busy = busyId === d.id;
                const active = ACTIVE_DELIVERY_STATUSES.includes(d.status);
                const ago = active ? minutesAgo(d.lastLocationAt, now) : null;
                return (
                  <TR key={d.id}>
                    <TD>
                      <StatusBadge status={d.status} />
                    </TD>
                    <TD>
                      <span className="font-mono text-xs" title={d.orderId}>
                        #{d.orderId.slice(-6)}
                      </span>
                    </TD>
                    <TD>{branchName(d.branchId)}</TD>
                    <TD>{driverName(d.driverId) ?? t('delivery.board.unassignedLabel')}</TD>
                    <TD>
                      <Money amount={d.feeAmount} branchId={d.branchId} />
                    </TD>
                    <TD>
                      {!active ? (
                        <span aria-hidden className="text-ink-muted">
                          —
                        </span>
                      ) : d.lastLat === null || d.lastLng === null ? (
                        <span className="text-ink-muted">{t('delivery.board.noPositionYet')}</span>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-ink-muted">
                            {t('delivery.board.positionAgo', { minutes: ago ?? 0 })}
                          </span>
                          <a
                            href={`https://www.google.com/maps?q=${d.lastLat},${d.lastLng}`}
                            target="_blank"
                            rel="noreferrer"
                            title={t('delivery.board.openInMaps')}
                            className="text-accent"
                          >
                            <Icon as={ExternalLink} size="sm" />
                          </a>
                        </div>
                      )}
                    </TD>
                    <TD className="text-ink-muted">{formatDateTime(d.updatedAt, i18n.language)}</TD>
                    <TD>
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDetailTarget(d)}
                          title={t('delivery.board.viewDetail')}
                        >
                          <Icon as={Eye} />
                        </Button>

                        {d.status === 'PENDING' && canDispatch && (
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => setAssignTarget(d)}>
                            <Icon as={UserPlus} />
                            {t('delivery.board.assign')}
                          </Button>
                        )}

                        {d.status === 'ASSIGNED' && canDispatch && (
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void unassign(d)}>
                            <Icon as={UserMinus} />
                          </Button>
                        )}

                        {d.status === 'ASSIGNED' && canUpdateStatus && (
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => void pickUp(d)}>
                            <Icon as={PackageCheck} />
                            {t('delivery.board.pickUp')}
                          </Button>
                        )}

                        {d.status === 'PICKED_UP' && canUpdateStatus && (
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => void enRoute(d)}>
                            <Icon as={Navigation} />
                            {t('delivery.board.enRouteAction')}
                          </Button>
                        )}

                        {d.status === 'EN_ROUTE' && canUpdateStatus && (
                          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setDeliverTarget(d)}>
                            <Icon as={Truck} />
                            {t('delivery.board.deliver')}
                          </Button>
                        )}

                        {(d.status === 'PICKED_UP' || d.status === 'EN_ROUTE') && canUpdateStatus && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => void fail(d)}
                            title={t('delivery.board.fail')}
                          >
                            <Icon as={XCircle} className="text-red-600 dark:text-red-400" />
                          </Button>
                        )}

                        {(d.status === 'PENDING' || d.status === 'ASSIGNED' || d.status === 'PICKED_UP') &&
                          canDispatch && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busy}
                              onClick={() => void cancel(d)}
                              title={t('delivery.board.cancel')}
                            >
                              <Icon as={Ban} className="text-red-600 dark:text-red-400" />
                            </Button>
                          )}

                        {d.status === 'FAILED' && canManageReturn && !d.returnDisposition && (
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => setReturnTarget(d)}>
                            {t('delivery.board.resolveReturn')}
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      {assignTarget && (
        <AssignDialog
          delivery={assignTarget}
          drivers={drivers}
          driverName={driverName}
          onClose={() => setAssignTarget(null)}
          onDone={reload}
        />
      )}
      {deliverTarget && (
        <DeliverDialog delivery={deliverTarget} onClose={() => setDeliverTarget(null)} onDone={reload} />
      )}
      {returnTarget && (
        <ResolveReturnDialog delivery={returnTarget} onClose={() => setReturnTarget(null)} onDone={reload} />
      )}
      {detailTarget && (
        <DeliveryDetailDialog
          delivery={detailTarget}
          branchName={branchName}
          driverName={driverName}
          onCopyLink={() => void copyTrackingLink(detailTarget)}
          onClose={() => setDetailTarget(null)}
        />
      )}
    </div>
  );
}

function AssignDialog({
  delivery,
  drivers,
  driverName,
  onClose,
  onDone,
}: {
  delivery: Delivery;
  drivers: Driver[];
  driverName: (id: string | null) => string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [driverId, setDriverId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const eligible = drivers.filter((d) => d.status === 'ACTIVE');

  const submit = async () => {
    if (!driverId) return;
    setSubmitting(true);
    try {
      await api.post(DELIVERY_ROUTES.assign(delivery.id), { driverId });
      toast.success(t('delivery.board.assigned'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.board.actionFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('delivery.board.assignTitle')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting || !driverId}>
            {t('delivery.board.assign')}
          </Button>
        </>
      }
    >
      {eligible.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('delivery.board.noEligibleDrivers')}</p>
      ) : (
        <Field label={t('delivery.board.driver')} required>
          <Select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
            <option value="">{t('delivery.board.driverPlaceholder')}</option>
            {eligible.map((d) => (
              <option key={d.id} value={d.id}>
                {driverName(d.id)}
              </option>
            ))}
          </Select>
        </Field>
      )}
    </Dialog>
  );
}

function DeliveryDetailDialog({
  delivery,
  branchName,
  driverName,
  onCopyLink,
  onClose,
}: {
  delivery: Delivery;
  branchName: (id: string) => string;
  driverName: (id: string | null) => string | null;
  onCopyLink: () => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const terminal = TERMINAL_DELIVERY_STATUSES.includes(delivery.status);
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('delivery.board.detailTitle')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
          {/*
            Copy only — never rendered as visible text or an `<a href>`. The
            token is a bearer secret (redteam-hardened to 192 bits); a clickable
            link would put it in this app's own address bar and browser
            history, which is exactly the kind of place the task's own note
            says not to put it.
          */}
          <Button onClick={onCopyLink}>
            <Icon as={Copy} />
            {t('delivery.board.copyTrackingLink')}
          </Button>
        </>
      }
    >
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Row label={t('delivery.board.status')} value={t(`delivery.status.${delivery.status}`)} />
        <Row label={t('delivery.board.branch')} value={branchName(delivery.branchId)} />
        <Row label={t('delivery.board.driver')} value={driverName(delivery.driverId) ?? t('delivery.board.unassignedLabel')} />
        <Row label={t('delivery.board.fee')} value={<Money amount={delivery.feeAmount} branchId={delivery.branchId} />} />
        <Row label={t('delivery.board.assignedAt')} value={formatDateTime(delivery.assignedAt, i18n.language)} />
        <Row label={t('delivery.board.pickedUpAt')} value={formatDateTime(delivery.pickedUpAt, i18n.language)} />
        <Row label={t('delivery.board.enRouteAt')} value={formatDateTime(delivery.enRouteAt, i18n.language)} />
        <Row
          label={t('delivery.board.deliveredOrFailedAt')}
          value={formatDateTime(delivery.deliveredAt ?? delivery.failedAt, i18n.language)}
        />
        {delivery.tenderMethod && (
          <Row label={t('delivery.board.tenderMethod')} value={t(`billing.method.${delivery.tenderMethod}`)} />
        )}
        {delivery.collectedAmount !== null && (
          <Row
            label={t('delivery.board.collectedAmountLabel')}
            value={<Money amount={delivery.collectedAmount} branchId={delivery.branchId} />}
          />
        )}
        {delivery.failureReason && (
          <Row label={t('delivery.board.failReason')} value={delivery.failureReason} />
        )}
        {delivery.returnDisposition && (
          <Row label={t('delivery.board.disposition')} value={t(`delivery.returnDisposition.${delivery.returnDisposition}`)} />
        )}
      </dl>
      {terminal && (
        <p className="mt-3 text-xs text-ink-muted">{t('delivery.board.trackingLinkExpiryHint')}</p>
      )}
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="contents">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
